import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  Paper,
} from "@mui/material";
import SchoolsDropdownById from "../../SchoolUsers/inc/SchoolsDropdownById";
import useData from "../../SchoolUsers/hooks/useSchoolDropdown";
import Mou from "../../CcpSchool/CcpSchoolAccountProfile/inc/Mou";
import { useTheme } from "@mui/material/styles";
import StudentApplicationApproval from "../../CcpSchool/CcpSchoolAppsAndTesting/inc/StudentApplicationApproval";
import AdditionalReports from "../../CcpSchool/CcpSchoolAppsAndTesting/inc/AdditionalReports";
import EligibilityReport from "../../CcpSchool/CcpSchoolAppsAndTesting/inc/EligibilityReport";
import RegReport from "../../CcpSchool/CcpSchoolReports/inc/RegReport";
import GradeReport from "../../CcpSchool/CcpSchoolReports/inc/GradeReport";
import AdditionalGradeReport from "../../CcpSchool/CcpSchoolReports/inc/AdditionalGradeReport";
import AdditionalRegReport from "../../CcpSchool/CcpSchoolReports/inc/AdditionalRegReport";
import CoursePlanning from "../../CcpSchool/CcpSchoolRestrictedCourses/inc/CoursePlanning";
import RostersInProgress from "../../CcpSchool/CcpSchoolRestrictedCourses/inc/RostersInProgress";
import AdditionalReport from "../../CcpSchool/CcpSchoolRestrictedCourses/inc/AdditionalReport";
import HeadCount from "../../CcpSchool/CcpStatsAndTrends/inc/HeadCount"; // Import HeadCount
import CreditHours from "../../CcpSchool/CcpStatsAndTrends/inc/CreditHours"; // Import CreditHours
import Top10Courses from "../../CcpSchool/CcpStatsAndTrends/inc/Top10Courses"; // Import Top10Courses
import Gender from "../../CcpSchool/CcpStatsAndTrends/inc/Gender"; // Import Gender
import GradeLevel from "../../CcpSchool/CcpStatsAndTrends/inc/GradeLevel"; // Import GradeLevel
import CourseModality from "../../CcpSchool/CcpStatsAndTrends/inc/CourseModality"; // Import CourseModality

// Component to render content for each tab
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Function to create accessibility properties for each tab
function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const CcpAdminSchoolView = () => {
  const [school, setSchool] = useState({ school_name: "", school_id: "" });
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const { data: schools, loading: schoolsLoading } = useData();
  const theme = useTheme();
  const linkColor = theme.palette.mode === "dark" ? "white" : "black";

  useEffect(() => {
    if (schools && schools.length > 0) {
      setSchool(schools[0]); // Select the first school by default
    }
  }, [schools]);

  // Tab titles
  const tabs = [
    "Account Profile",
    "Apps and Testing",
    "Reports",
    "Restricted Courses",
    "Stats and Trends",
  ];

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handle school change in dropdown
  const handleSchoolChange = (newSchool) => {
    if (newSchool) {
      //only change if a school is actually passed
      setSchool(newSchool);
      setLoadingTabs(true);
      setSelectedTab(0); // Reset to first tab on school change
      // Simulate loading the tabs (replace with actual data fetching)
      const timer = setTimeout(() => {
        setLoadingTabs(false);
      }, 1000); // Adjust time as needed
      return () => clearTimeout(timer);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", p: 2, mt: 10 }}>
      <Typography variant="h5" gutterBottom>
        CCP Admin School View
      </Typography>

      {schoolsLoading ? (
        <CircularProgress />
      ) : (
        <SchoolsDropdownById
          schools={schools}
          value={school}
          onChange={handleSchoolChange}
        />
      )}

      {school.school_id && (
        <Box sx={{ display: "flex", mt: 2 }}>
          {loadingTabs ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <CircularProgress />
              <Typography mt={2}>Loading...</Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, display: "flex" }}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="Vertical tabs"
                sx={{ borderRight: 1, borderColor: "divider" }}
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab} {...a11yProps(index)} />
                ))}
              </Tabs>

              <Paper sx={{ flexGrow: 1, p: 2 }}>
                {tabs.map((tab, index) => (
                  <TabPanel key={index} value={selectedTab} index={index}>
                    {/* Content for each tab */}
                    {index === 0 && ( // Account Profile tab (index 0)
                      <Box>
                        <Mou
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                      </Box>
                    )}
                    {index === 1 && ( // Apps and Testing tab (index 1)
                      <Box>
                        <StudentApplicationApproval
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <AdditionalReports
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <EligibilityReport
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                      </Box>
                    )}
                    {index === 2 && ( // Reports tab (index 2)
                      <Box>
                        <RegReport
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <GradeReport
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <AdditionalGradeReport
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <AdditionalRegReport
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                      </Box>
                    )}
                    {index === 3 && ( // Restricted Courses Tab (index 3)
                      <Box>
                        <CoursePlanning
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <RostersInProgress
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <AdditionalReport
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                      </Box>
                    )}
                    {index === 4 && ( // Stats and Trends Tab (index 4)
                      <Box>
                        <HeadCount
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <CreditHours
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <Top10Courses
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <Gender
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <GradeLevel
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                        <CourseModality
                          schoolId={school.school_id}
                          linkColor={linkColor}
                        />
                      </Box>
                    )}
                    {index !== 0 &&
                      index !== 1 &&
                      index !== 2 &&
                      index !== 3 &&
                      index !== 4 && (
                        <Typography>
                          {tab} Content for {school.school_name}
                        </Typography>
                      )}

                    {/* Add more content or other components as needed */}
                  </TabPanel>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CcpAdminSchoolView;
